import { Api } from "lavva.exalushome";
import { DataFrame, Method, Status } from "lavva.exalushome/build/js/DataFrame";
import { IDevice } from "lavva.exalushome/build/js/Services/Devices/IDevice";
import { ExalusConnectionService } from "lavva.exalushome/build/js/Services/ExalusConnectionService";
import { ResponseResult } from "lavva.exalushome/build/js/Services/FieldChangeResult";
import { IExalusConnectionService } from "lavva.exalushome/build/js/Services/IExalusConnectionService";
import { ILoggerService } from "lavva.exalushome/build/js/Services/Logging/ILoggerService";
import { LoggerService } from "lavva.exalushome/build/js/Services/Logging/LoggerService";
import { GetChannelParamsRequest } from "../DataRequest";
import { GlobalTimeParameters, Hours, IInputOutputSwitchParameters, InputModeSwitch, InputModeSwitchParams, InputOutputSwitchParameters, Minutes, ReactionToInput, ReactionToInputSwitchParams, Seconds, StateOfTurnOnSwitchDeviceParameters, SwitchTurnOnState, Time as TimeParam } from "./OnOffSwitchParameters";
//import { InputOutputSwitchParameters }

export class OnOffSwitchService {
    protected _connection: IExalusConnectionService | null = null;
    protected _logger: ILoggerService;;
    public static readonly ServiceName: string = "OnOffSwitchService";

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
        this._logger = Api.Get<ILoggerService>(LoggerService.ServiceName);
    }

    async GetTurnOnBehaviourAsync(device: IDevice, channel: number): Promise<StateOfTurnOnSwitchDeviceParameters | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get turn on behaviour - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<StateOfTurnOnSwitchDeviceParameters>(new GetTurnOnBehaviourRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on behaviour - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on behaviour - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get turn on behaviour - no data in response.`);

            const data = new StateOfTurnOnSwitchDeviceParameters();
            data.Channel = result.Data.Channel;
            data.State = result.Data.State;

            //for some reasons, as default ROP sends 16 when state was not set after reset (?) 
            if (data.State === 16 as number)
                data.State = SwitchTurnOnState.Disabled;

            return data;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Failed to retrieve turn on behaviour - unknown error.`);
        }
    }

    async SetTurnOnBehaviourAsync(device: IDevice, params: StateOfTurnOnSwitchDeviceParameters): Promise<Status> {
        try {
            const request = new StateOfTurnOnDeviceParametersFrame();
            request.DeviceGuid = device.Guid;
            request.Channel = params.Channel;
            request.State = params.State;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetTurnOffBehaviourRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set turn on behaviour - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set turn on behaviour, response status: ${result.Status}`);

            return result.Status;

        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetGlobalTimeSettingsAsync(device: IDevice, channel: number): Promise<GlobalTimeParameters | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get global time settings - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<GlobalTimeParametersFrame>(new GetGlobalTimeSettingsRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get global time settings - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get global time settings - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get global time settings - no data in response.`);

            const data = new GlobalTimeParameters();
            data.Channel = result.Data.Channel;
            data.Time = this.ParseNumberToTime(result.Data.Time);

            return data;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get global time settings - unknown error.`);
        }
    }

    async SetGlobalTimeSettingsAsync(device: IDevice, params: GlobalTimeParameters): Promise<Status> {
        try {
            const time = params.Time.Hours * 3600 + params.Time.Minutes * 60 + params.Time.Seconds;

            const request = new GlobalTimeParametersFrame();
            request.Channel = params.Channel;
            request.DeviceGuid = device.Guid;
            request.Time = time;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetGlobalTimeSettingsRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set global time settings - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set global time settings, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetTurnOnTimeAsync(device: IDevice, channel: number): Promise<GlobalTimeParameters | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get turn on time - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<GlobalTimeParametersFrame>(new GetTurnOnTimeSettingsRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on time - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on time - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get turn on time - no data in response.`);

            const data = new GlobalTimeParameters();
            data.Channel = result.Data.Channel;
            data.Time = this.ParseNumberToTime(result.Data.Time);

            return data;
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get turn on time - unknown error.`);
        }
    }

    async SetTurnOnTimeAsync(device: IDevice, params: GlobalTimeParameters): Promise<Status> {
        try {
            const time = params.Time.Hours * 3600 + params.Time.Minutes * 60 + params.Time.Seconds;

            const request = new GlobalTimeParametersFrame();
            request.Channel = params.Channel;
            request.DeviceGuid = device.Guid;
            request.Time = time;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetTurnOnTimeSettingsRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set turn on time settings - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set turn on time settings, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async GetSwitchInputOutputParamAsync(device: IDevice, channel: number): Promise<IInputOutputSwitchParameters[] | ResponseResult<SwitchConfigurationErrorCode>> {
        try {
            if (!device.Channels.any(ch => ch.Number === channel))
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.InvalidChannelNumber, `Cannot get input/output parameters - invalid channel number, channel: ${channel}`);

            const result = await this._connection?.SendAndWaitForResponseAsync<InputOutputSwitchParametersLocal[]>(new GetSwitchInputOutputParamRequest(new GetChannelParamsRequest(device.Guid, channel)), 8000, false);

            if (result == null || result.Status == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get input/output parameters - unknown error.`);

            if (result.Status !== Status.OK)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.OtherError, `Cannot get input/output parameters - controller responded with error code: ${result.Status}.`);

            if (result.Data == null)
                return new ResponseResult<SwitchConfigurationErrorCode>(SwitchConfigurationErrorCode.NoData, `Cannot get input/output parameter - no data in response.`);

            return result.Data.map(param => {
                const res = new InputOutputSwitchParametersLocal();
                res.Channel = param.Channel;
                res.InputMode = param.InputMode;
                res.InputsNumber = param.InputsNumber;
                res.ReactionToInput = param.ReactionToInput;
                res.InputChannel = param.InputChannel;

                return res;
            });
        } catch (error) {
            return new ResponseResult(SwitchConfigurationErrorCode.OtherError, `Cannot get input/output parameters - unknown error.`);
        }
    }

    async SetSwitchInputOutputParamAsync(device: IDevice, params: InputOutputSwitchParameters): Promise<Status> {
        try {
            if (params.InputMode == InputModeSwitch.Bistable && params.ReactionToInput == ReactionToInput.Bi) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - reaction to input cannot be 'bistable' when mode is set to 'bistable', switch mode to 'monostable' and call fucntion again.`);
                return Status.WrongData;
            }

            const request = new InputOutputSwitchParametersFrame();
            request.DeviceGuid = device.Guid;
            request.Channel = params.Channel;
            request.InputChannel = params.InputChannel;
            request.ReactionToInput = params.ReactionToInput;
            request.InputMode = params.InputMode;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetSwitchInputOutputParamRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async SetSwitchInputOutputInputModeParamAsync(device: IDevice, params: InputModeSwitchParams): Promise<Status> {
        try {
            const current = await this.GetSwitchInputOutputParamAsync(device, params.Channel);

            if ((current as ResponseResult<SwitchConfigurationErrorCode>).Type != null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - cannot get current configuration.`);
                return Status.Error;
            }

            const currentConfig = (current as IInputOutputSwitchParameters[]).firstOrDefault(e => e.Channel == params.Channel && e.InputChannel == params.InputChannel);

            if (currentConfig == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - channel or inputChannel params may be incorrect.`);
                return Status.WrongData;
            }

            const request = new InputOutputSwitchParametersFrame();
            request.DeviceGuid = device.Guid;
            request.Channel = params.Channel;
            request.InputChannel = params.InputChannel;
            request.InputMode = params.InputMode;
            request.ReactionToInput = currentConfig.ReactionToInput;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetSwitchInputOutputParamRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    async SetSwitchInputOutputReactionToInputParamAsync(device: IDevice, params: ReactionToInputSwitchParams): Promise<Status> {
        try {
            const current = await this.GetSwitchInputOutputParamAsync(device, params.Channel);

            if ((current as ResponseResult<SwitchConfigurationErrorCode>).Type != null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - cannot get current configuration.`);
                return Status.Error;
            }

            const currentConfig = (current as IInputOutputSwitchParameters[]).firstOrDefault(e => e.Channel == params.Channel && e.InputChannel == params.InputChannel);

            if (currentConfig == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - channel or inputChannel params may be incorrect.`);
                return Status.WrongData;
            }

            if (currentConfig.InputMode == InputModeSwitch.Bistable && params.ReactionToInput == ReactionToInput.Bi) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - reaction to input cannot be 'bistable' when mode is set to 'bistable', switch mode to 'monostable' and call fucntion again.`);
                return Status.WrongData;
            }

            const request = new InputOutputSwitchParametersFrame();
            request.DeviceGuid = device.Guid;
            request.Channel = params.Channel;
            request.InputChannel = params.InputChannel;
            request.ReactionToInput = params.ReactionToInput;
            request.InputMode = currentConfig.InputMode;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetSwitchInputOutputParamRequest(request), 8000, false);

            if (result == null || result.Status == null) {
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                this._logger.Error(OnOffSwitchService.ServiceName, `Failed to set input/output parameters, response status: ${result.Status}`);

            return result.Status;
        } catch (error) {
            return Status.FatalError;
        }
    }

    protected ParseNumberToTime(data: number): Time {
        const result = new Time();
        result.Hours = Math.floor(data / 3600) as Hours;
        result.Minutes = Math.floor((data % 3600) / 60) as Minutes;
        result.Seconds = Math.floor((data % 3600) % 60) as Seconds;
        result.Raw = data;

        return result;
    }
}

export enum SwitchConfigurationErrorCode {
    InvalidChannelNumber = "InvalidChannelNumber",
    OtherError = "OtherError",
    NoData = "NoData"
}

//TurnOn behaviour
class GetTurnOnBehaviourRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/state/turnon";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class StateOfTurnOnDeviceParametersFrame extends StateOfTurnOnSwitchDeviceParameters {
    DeviceGuid: string = "";
}

class SetTurnOffBehaviourRequest extends DataFrame<StateOfTurnOnSwitchDeviceParameters> {
    constructor(data: StateOfTurnOnSwitchDeviceParameters) {
        super();
        this.Resource = "/extalife/device/parameters/state/turnon";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//Global time
class Time extends TimeParam {
    public set Raw(value: number) {
        this._raw = value;
    }
}

class GetGlobalTimeSettingsRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/global/time";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetGlobalTimeSettingsRequest extends DataFrame<GlobalTimeParametersFrame> {
    constructor(data: GlobalTimeParametersFrame) {
        super();
        this.Resource = "/extalife/device/parameters/global/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

class GlobalTimeParametersFrame {
    Channel: number = 0;
    DeviceGuid: string = "";
    Time: number = 0;
}

//TurnOn time
class GetTurnOnTimeSettingsRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/device/on/time";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetTurnOnTimeSettingsRequest extends DataFrame<GlobalTimeParametersFrame> {
    constructor(data: GlobalTimeParametersFrame) {
        super();
        this.Resource = "/extalife/device/parameters/device/on/time";
        this.Method = Method.Put;
        this.Data = data;
    }
}

//Input/output
class InputOutputSwitchParametersLocal implements IInputOutputSwitchParameters {
    private _channel: number = 0;
    private _inputsNumber: number = 0;
    private _inputMode: InputModeSwitch = InputModeSwitch.Monostable;
    private _reactionToInput: ReactionToInput = ReactionToInput.None;
    private _inputChannel: number = 0;

    public get Channel(): number {
        return this._channel;
    }
    public set Channel(value: number) {
        this._channel = value;
    }
    public get InputsNumber(): number {
        return this._inputsNumber;
    }
    public set InputsNumber(value: number) {
        this._inputsNumber = value;
    }
    public get InputMode(): InputModeSwitch {
        return this._inputMode;
    }
    public set InputMode(value: InputModeSwitch) {
        this._inputMode = value;
    }
    public get ReactionToInput(): ReactionToInput {
        return this._reactionToInput;
    }
    public set ReactionToInput(value: ReactionToInput) {
        this._reactionToInput = value;
    }
    public get InputChannel(): number {
        return this._inputChannel;
    }
    public set InputChannel(value: number) {
        this._inputChannel = value;
    }
}

class InputOutputSwitchParametersFrame {
    DeviceGuid: string = "";
    Channel: number = 0;
    InputChannel: number = 0;
    InputMode: InputModeSwitch = InputModeSwitch.Bistable;
    ReactionToInput: ReactionToInput = ReactionToInput.None;
}

class GetSwitchInputOutputParamRequest extends DataFrame<GetChannelParamsRequest> {
    constructor(data: GetChannelParamsRequest) {
        super();
        this.Resource = "/extalife/device/parameters/input/output";
        this.Method = Method.Get;
        this.Data = data;
    }
}

class SetSwitchInputOutputParamRequest extends DataFrame<InputOutputSwitchParametersFrame> {
    constructor(data: InputOutputSwitchParametersFrame) {
        super();
        this.Resource = "/extalife/device/parameters/input/output";
        this.Method = Method.Put;
        this.Data = data;
    }
}